import {
  Typography,
  Button,
  Controller,
  TextEditor,
  useForm,
  BottomNavigation,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import {
  EditRoleDocument,
  TagEntityType,
  CreateRoleDocument,
} from 'src/graphql/generated/graphql';
import 'react-quill/dist/quill.snow.css';

import styles from './index.module.css';
import { DocumentNode, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { TagsField } from 'src/components/common/form/fields/tags';
import { PermissionsFormList } from '../../common/form/permissions';
import { EditRoleItem } from 'src/components/roles/form/types';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { NameField } from 'src/components/roles/form/fields/name';
import { SelectedPermission } from 'src/components/common/form/permissions/types';

type Form = {
  name: string;
  description?: string;
  tagIds?: string[];
};

interface FormProps {
  onFinish?: () => void;
  onClose?: () => void;
  role?: EditRoleItem;
  refetchQueries?: DocumentNode[];
  title?: React.ReactNode | string;
}

const getFormData = (role?: EditRoleItem) => ({
  name: role?.name || '',
  description: role?.description || undefined,
  tagIds: role?.tags?.map((tag) => tag.id) || [],
  permissionIds: role?.permissions?.map((permission) => permission.id) || [],
});

export const RoleForm = ({
  onFinish,
  role,
  refetchQueries,
  onClose,
}: FormProps) => {
  const { t } = useTranslation();
  const [selectedPermissions, setSelectedPermissions] = useState<
    SelectedPermission[]
  >([]);

  const [createRole, { loading: loadingCreate }] = useMutation(
    CreateRoleDocument,
    {
      refetchQueries,
    },
  );

  const [updateRole, { loading: loadingUpdate }] = useMutation(
    EditRoleDocument,
    {
      refetchQueries,
    },
  );

  const loading = loadingCreate || loadingUpdate;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setError,
  } = useForm<Form>({
    defaultValues: getFormData(role),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!role) {
      return;
    }

    const formData = getFormData(role);
    reset(formData);
    setSelectedPermissions(
      role?.permissions?.map((permission) => permission) || [],
    );
  }, [role, reset]);

  const onCancel = () => {
    onClose?.();
  };

  const onSubmit = async (form: Form) => {
    const payload = {
      ...form,
      permissionIds: selectedPermissions.map((permission) => permission.id),
    };

    if (role?.id) {
      await updateRole({
        variables: {
          id: role.id,
          data: payload,
        },
      });
    } else {
      await createRole({
        variables: {
          data: payload,
        },
      });
    }

    onFinish?.();
  };

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TableWrapper>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={styles.fieldsWrapper}>
              <div className={styles.fieldWrapper}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="min-w-[96px] uppercase"
                >
                  {t('common.name')}
                </Typography>

                <NameField
                  control={control}
                  setError={setError}
                  errors={errors}
                  roleId={role?.id}
                />
              </div>

              <div className={styles.fieldWrapper}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="min-w-[96px] uppercase"
                >
                  {t('common.tags')}
                </Typography>
                <Controller
                  name="tagIds"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <TagsField field={field} entityType={TagEntityType.Role} />
                  )}
                />
              </div>
            </div>
            <div className={styles.fieldsWrapper}>
              <div className={styles.fieldWrapper}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="min-w-[96px] uppercase"
                >
                  {t('common.description')}
                </Typography>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <TextEditor
                      {...field}
                      defaultValue={field.value}
                      className="w-full"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </TableWrapper>

        <TableWrapper className="mt-6">
          <PermissionsFormList
            onChange={setSelectedPermissions}
            selectedPermissions={selectedPermissions}
          />
        </TableWrapper>

        <BottomNavigation>
          <Button
            className="mr-2"
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            disabled={loading}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={onCancel}
            className="mr-4"
          >
            {t('common.close')}
          </Button>
        </BottomNavigation>
      </form>
    </ContentWrapper>
  );
};
